import Link from "next/link";
import type { LucideIcon } from "lucide-react";
import {
  MessageCircleMore,
  Sparkles,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { whatsappHref } from "@/lib/whatsapp";

const WHATSAPP_LINK = whatsappHref(
  "Hi Anchor Sprint, I'd like to book a free consultation (from your homepage).",
);

const highlights: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "45-minute executive workshop",
    description:
      "Facilitate alignment across sponsors and delivery leads while our team maps the critical pain points.",
    icon: MessageCircleMore,
  },
  {
    title: "ROI & compliance analysis",
    description:
      "Receive quantified benefits, risk considerations, and Malaysia Digital Status guardrails in one briefing.",
    icon: Sparkles,
  },
  {
    title: "Board-ready implementation pack",
    description:
      "Leave with a sequenced roadmap, investment envelope, and governance structure ready for approval.",
    icon: ShieldCheck,
  },
];

interface ConsultationCTASectionProps {
  locale?: Locale;
}

export default function ConsultationCTASection({
  locale = defaultLocale,
}: ConsultationCTASectionProps) {
  return (
    <section className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* The page's single dark moment */}
        <div className="rounded-3xl bg-hero-background px-6 py-16 md:px-12 lg:px-16 lg:py-20">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-primary-contrast/60">
              {t(locale, "Free consultation")}
            </p>
            <h2 className="mt-4 font-display text-4xl font-bold text-primary-contrast md:text-5xl lg:text-6xl">
              {t(locale, "Ready to put")}
              <br />
              <span className="emphasis-underline">
                {t(locale, "AI agents to work?")}
              </span>
            </h2>
            <p className="mt-6 mx-auto max-w-2xl text-lg text-primary-contrast/70">
              {t(
                locale,
                "Get expert guidance on AI solutions, digital transformation and custom software development for Malaysian businesses. To get found on Google and in ChatGPT, work with our",
              )}{" "}
              <Link
                href={getLocalizedPathname("/services/seo-services-malaysia/", locale)}
                className="underline decoration-primary-contrast/40 underline-offset-2 hover:text-primary-contrast"
              >
                {t(locale, "AI SEO agency in Malaysia")}
              </Link>
              {locale === "zh" ? "。" : "."}
            </p>
          </div>

          {/* Highlights */}
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {highlights.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="rounded-2xl border border-primary-contrast/10 p-6"
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-2 text-base font-semibold text-primary-contrast">
                  {t(locale, title)}
                </h3>
                <p className="text-sm leading-relaxed text-primary-contrast/70">
                  {t(locale, description)}
                </p>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="mt-12 text-center">
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center sm:items-center">
              {/* Primary CTA - blue pill */}
              <Link
                href={getLocalizedPathname("/contact/", locale)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground transition hover:bg-primary-emphasis sm:min-w-[240px]"
              >
                {t(locale, "Book a consultation")}
              </Link>

              {/* Secondary CTA - WhatsApp, stock green */}
              <Link
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-track-event="contact_whatsapp_click"
                data-track-location="home_consultation_cta"
                data-track-phone="601159246128"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 font-semibold text-white transition hover:bg-[#25D366]/90 sm:min-w-[240px]"
              >
                <MessageCircleMore className="h-5 w-5" aria-hidden />
                {t(locale, "Chat on WhatsApp")}
              </Link>
            </div>

            {/* Response time indicator */}
            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-primary-contrast/10 px-5 py-2.5">
              <Zap className="h-4 w-4 text-success" aria-hidden />
              <p className="text-sm text-primary-contrast/70">
                {t(locale, "Response within")}{" "}
                <span className="font-medium text-primary-contrast">
                  {t(locale, "one business day")}
                </span>{" "}
                {t(locale, "with confirmed meeting time")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
